/**
 * Who can get to the ball, and who has it.
 *
 * Two questions, asked constantly, that between them decide almost everything about who
 * does what: how long would it take this player to reach that spot, and is the ball
 * effectively under his control right now.
 *
 * The first is not a distance divided by a speed. It is a small forward simulation that
 * respects the fact that a running player cannot stop or turn instantly: his existing
 * momentum is faded out over 700 ms while a reach radius grows around him, and the answer
 * is the first moment that radius covers the target. A player sprinting away from the ball
 * therefore correctly reports a much longer time than one standing the same distance away.
 *
 * Source: AIfunctions.cpp:516-641 and :941-961, player.cpp:163-263.
 */

import { Vector3 } from 'three';
import { clamp, normalizedClamp, normalizedOrZero } from '../core/math';
import { BALL_PREDICTION_MS } from '../core/constants';
import { DEFAULT_PLAYER_HEIGHT, DEFAULT_TOUCH_OFFSET_MS } from './anim/touch';
import { IDLE_DRIBBLE_SWITCH } from './anim/velocity';
import type { Ball } from './ball';

export interface TimeNeeded {
  /** What he will realistically manage. */
  usualMs: number;
  /** What he would manage with a full stretch — a lunge, a toe. Feeds the last-ditch rule. */
  optimisticMs: number;
}

const _scratchA = new Vector3();
const _scratchB = new Vector3();

/**
 * How long this player needs to reach a point — `AI_GetTimeNeededForDistance_ms`.
 *
 * `precise` widens the band inside which the real simulation runs rather than the cheap
 * distance estimate; the original grants it only to the player his team has already
 * designated, so the expensive answer is computed once per team rather than eleven times.
 */
export function timeNeededForDistanceMs(
  playerPos: Vector3,
  playerMovement: Vector3,
  targetPos: Vector3,
  maxVelocity: number,
  precise: boolean,
  maxTimeMs = -1,
): TimeNeeded {
  const optimizeDist = precise ? 48.0 : 16.0;

  const initialDist = playerPos.distanceTo(targetPos);
  const defaultOptimizedTimeMs = Math.round(
    (_scratchA.copy(playerPos).addScaledVector(playerMovement, 0.2).distanceTo(targetPos) /
      (maxVelocity * 0.75)) *
      1000,
  );
  if (initialDist > optimizeDist) {
    return { usualMs: defaultOptimizedTimeMs, optimisticMs: defaultOptimizedTimeMs - 200 };
  }

  const currentPos = _scratchA.copy(playerPos);
  const currentMovement = _scratchB.copy(playerMovement);

  // Aim a little in front of the feet: that is where a ball can actually be played.
  const ffo = 0.1;
  if (currentMovement.length() > IDLE_DRIBBLE_SWITCH) {
    currentPos.addScaledVector(normalizedOrZero(currentMovement, _ffo), ffo);
    currentPos.addScaledVector(currentMovement, 0.01);
  } else {
    _ffo.copy(targetPos).sub(playerPos);
    currentPos.addScaledVector(normalizedOrZero(_ffo, _ffo), ffo);
  }

  let currentTimeMs = 0;
  const timeStepMs = 10;
  const changeTimeMs = 700;
  // How far he can stretch: a normal reach, and the desperate one.
  let radiusUsual = 0.28;
  let radiusOptimistic = 0.9;
  let resultingRadiusUsual = radiusUsual;
  let foundOptimistic = false;
  let usualMs = 0;
  let optimisticMs = 0;

  // Not quite his top speed: the last fraction of it is very hard to reach against drag.
  const adaptedMaxVelocity = maxVelocity * 0.94;

  for (;;) {
    const bias = clamp(currentTimeMs / changeTimeMs, 0, 1);

    if (bias >= 1) {
      // His old momentum is spent; from here it is plain distance over speed.
      const remainingUsual = clamp(currentPos.distanceTo(targetPos) - radiusUsual, 0, 100000);
      usualMs = currentTimeMs + (remainingUsual / adaptedMaxVelocity) * 1000;
      resultingRadiusUsual = radiusUsual + remainingUsual;
      if (!foundOptimistic) {
        const remainingOptimistic = clamp(
          currentPos.distanceTo(targetPos) - radiusOptimistic,
          0,
          100000,
        );
        optimisticMs = currentTimeMs + (remainingOptimistic / adaptedMaxVelocity) * 1000;
      }
      break;
    }

    currentMovement.x = playerMovement.x * (1 - bias);
    currentMovement.y = playerMovement.y * (1 - bias);
    currentPos.x += currentMovement.x * timeStepMs * 0.001;
    currentPos.y += currentMovement.y * timeStepMs * 0.001;

    radiusUsual += adaptedMaxVelocity * bias * timeStepMs * 0.001;
    radiusOptimistic += adaptedMaxVelocity * bias * timeStepMs * 0.001;

    const targetDistance = currentPos.distanceTo(targetPos);
    const outOfTime = maxTimeMs !== -1 && currentTimeMs > maxTimeMs;
    if ((targetDistance < radiusOptimistic || outOfTime) && !foundOptimistic) {
      optimisticMs = currentTimeMs;
      foundOptimistic = true;
    }
    if (targetDistance < radiusUsual || outOfTime) {
      resultingRadiusUsual = radiusUsual;
      usualMs = currentTimeMs;
      break;
    }

    currentTimeMs += timeStepMs;
  }

  if (maxTimeMs !== -1 && currentTimeMs > maxTimeMs) {
    usualMs = Math.max(defaultOptimizedTimeMs, (currentTimeMs + 100) * 2);
    if (!foundOptimistic) optimisticMs = usualMs;
    return { usualMs, optimisticMs };
  }

  // Already there. Report the fraction of a reach he still has to cover, so two players
  // both standing over the ball can still be told apart.
  if (usualMs === 0) {
    usualMs = Math.round(clamp(playerPos.distanceTo(targetPos) / resultingRadiusUsual, 0, 1) * 10);
    optimisticMs = usualMs;
  }

  return { usualMs, optimisticMs };
}

const _ffo = new Vector3();

export interface PossessionInputs {
  position: Vector3;
  movement: Vector3;
  directionVec: Vector3;
  /**
   * The highest ball he can play, in metres — `MatchPlayer.reachableBallHeight`. Moments of
   * the flight above it are not moments he can meet, so they are not moments to count. Absent
   * means the height every clip was drawn for.
   */
  reachableBallHeight?: number;
}

/**
 * Is the ball effectively his — `AI_HasPossession` (AIfunctions.cpp:941-961).
 *
 * Two conditions, both of which have to hold: the ball is inside a one-metre circle just in
 * front of him, and it is not moving very differently from the way he is. The second is what
 * stops a player being credited with possession of a ball that is merely passing through.
 */
export function hasPossession(ball: Ball, player: PossessionInputs): boolean {
  const ballNow = ball.predict(0, _ballNow);
  if (player.position.distanceTo(ballNow) > 5.0) return false;
  if (Math.abs(ballNow.z) > 0.5) return false;

  const centre = _centre
    .copy(player.position)
    .addScaledVector(player.movement, 0.05)
    .addScaledVector(player.directionVec, 0.1);
  const flat = _flat.set(ballNow.x - centre.x, ballNow.y - centre.y, 0);
  if (flat.length() > 1.0) return false;

  const ballMovement = ball
    .predict(10, _ballSoon)
    .sub(ballNow)
    .multiplyScalar(100);
  if (ballMovement.sub(player.movement).length() > 6.0) return false;

  return true;
}

const _ballNow = new Vector3();
const _ballSoon = new Vector3();
const _centre = new Vector3();
const _flat = new Vector3();

/**
 * How long until this player can meet the ball anywhere along its flight — the scan in
 * `Player::UpdatePossessionStats` (player.cpp:163-263).
 *
 * It walks the ball's forecast rather than aiming at where the ball is, which is why a
 * defender cuts a through-ball off instead of trailing it. The step is coarse at first and
 * refines once a hit is found, because the scan runs for twenty-two players every tick.
 */
export function timeNeededToGetToBallMs(
  ball: Ball,
  player: PossessionInputs & { maxVelocity: number },
  precise: boolean,
  startTimeMs = 0,
): TimeNeeded {
  const fallback = Math.max(
    BALL_PREDICTION_MS,
    Math.round(
      (ball
        .predict(BALL_PREDICTION_MS - 10, _target)
        .setZ(0)
        .distanceTo(_scratchA.copy(player.position).addScaledVector(player.movement, 0.2)) /
        (player.maxVelocity * 0.75)) *
        1000,
    ),
  );
  let usualMs = fallback;
  let optimisticMs = fallback;

  let refine = false;
  let timeStepMs = 10;
  let previousMs = 0;

  for (let ms = startTimeMs; ms < BALL_PREDICTION_MS; ms += timeStepMs) {
    const ballAt = ball.predict(ms, _target);
    // Was a flat 1.5 m, which is above a chest and below a head. See
    // `MatchPlayer.reachableBallHeight` for why both height gates now ask the same question.
    if (ballAt.z < (player.reachableBallHeight ?? DEFAULT_PLAYER_HEIGHT)) {
      const flat = _flatTarget.set(ballAt.x, ballAt.y, 0);
      const result = timeNeededForDistanceMs(
        player.position,
        player.movement,
        flat,
        player.maxVelocity,
        precise,
        ms,
      );

      if (result.optimisticMs <= ms && ms < optimisticMs) optimisticMs = ms;

      if (result.usualMs <= ms) {
        if (!refine) {
          // Back up and walk the last coarse step at full resolution.
          ms = previousMs;
          timeStepMs = 10;
          refine = true;
          continue;
        }
        usualMs = ms;
        break;
      }
    }

    if (!refine) {
      // The coarse step is "how long could the ball possibly take to get here", so the
      // scan skips ahead over stretches the ball cannot have reached yet.
      const ballDist = player.position.distanceTo(_target) + 0.2;
      const timeToGoMs = Math.round((ballDist / 50) * 1000);
      timeStepMs = Math.floor(clamp(timeToGoMs, 10, 500) / 10) * 10;
    } else {
      timeStepMs = 10;
    }

    previousMs = ms;
  }

  return { usualMs, optimisticMs };
}

const _target = new Vector3();
const _flatTarget = new Vector3();

/**
 * How long a player needs when the answer is "practically no time at all" — the rescale at
 * `player.cpp:262-266`, whose own comment reads "apply quantum mechanics on the scale of the
 * very small".
 *
 * The scan above walks the ball's forecast in whole ten-millisecond steps and stops at the
 * first step it can reach, so the smallest answer it is capable of giving is ten — and it
 * gives exactly ten to everybody who can reach the ball at all. Below the eighty milliseconds
 * a touch takes to land it has run out of resolution entirely.
 *
 * So the original throws that answer away and measures the geometry instead: where the player
 * will have walked to by the time a touch would land against where the ball will be then,
 * spread over nought to six-tenths of a metre and mapped back onto the same nought-to-eighty
 * scale. A ball dropping onto a player's foot and a ball already rolling off it then come out
 * tens of milliseconds apart instead of tied, and who is nominated to go for the ball, which
 * side is judged to have it and who is allowed to slide in all turn on that difference.
 *
 * Truncated to whole milliseconds, because the field the answer lands in is an integer
 * (`player.hpp:111-112`).
 */
export function touchOffsetTimeMs(ball: Ball, player: PossessionInputs): number {
  const at = _reach
    .copy(player.position)
    .addScaledVector(player.movement, DEFAULT_TOUCH_OFFSET_MS * 0.001);
  const ballAt = ball.predict(DEFAULT_TOUCH_OFFSET_MS, _reachBall);
  const gap = Math.hypot(at.x - ballAt.x, at.y - ballAt.y);
  return Math.floor(normalizedClamp(gap, 0, 0.6) * DEFAULT_TOUCH_OFFSET_MS);
}

const _reach = new Vector3();
const _reachBall = new Vector3();

/**
 * `Team::GetTeamPossessionAmount` (team.cpp:324) — above 1 means this side is closer to the
 * ball than the other, below 1 means it is chasing.
 */
export function teamPossessionAmount(ownTimeMs: number, otherTimeMs: number): number {
  return (otherTimeMs + 1500) / (ownTimeMs + 1500);
}

/**
 * `Player::AllowLastDitch` (player.cpp:141-144) — may this player try things he probably
 * cannot pull off?
 *
 * Yes when his side is behind in the race for the ball, or when the gap between what he
 * could manage at full stretch and what he will realistically manage is large enough that
 * the stretch is worth attempting.
 */
export function allowLastDitch(
  timeNeeded: TimeNeeded,
  ownTeamPossessionAmount: number,
  includingPossessionAmount = true,
): boolean {
  if (includingPossessionAmount && ownTeamPossessionAmount < 1.0) return true;
  return timeNeeded.optimisticMs * 1.7 + 800 < timeNeeded.usualMs;
}
